import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Settings, Star, MapPin, Calendar, Edit, Camera, ChevronRight, LogOut, Bell, Shield, HelpCircle, Package, ShoppingBag, RefreshCw, Wallet, Users, Store, TrendingUp, Heart } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { requestNotificationPermission, isNotificationEnabled, isPushServiceAvailable } from '../services/onesignal';
import { FoundingTenBanner } from '../components/common/FoundingTenBanner';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, switchRole, updateProfile, updateNotificationPreferences, getProductsBySeller } = useApp();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const avatarInputRef = useRef(null);
  const [editForm, setEditForm] = useState({
    name: '',
    bio: '',
    location: '',
    store_name: '',
    store_description: ''
  });
  const [storeForm, setStoreForm] = useState({
    store_name: '',
    store_description: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  if (!isAuthenticated) {
    return (
      <Layout title="Profile">
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <User className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Sign in to view profile</h2>
          <p className="text-gray-500 text-center mb-4">Access your listings, orders, and settings</p>
          <div className="flex gap-3">
            <Button onClick={() => navigate('/login')} className="bg-minted-600 hover:bg-minted-700">
              Sign In
            </Button>
            <Button onClick={() => navigate('/register')} variant="outline">
              Create Account
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const myListings = getProductsBySeller(user.id);
  const walletBalance = user?.wallet?.availableBalance ?? 0;

  const handleEditOpen = () => {
    setEditForm({
      name: user.name,
      bio: user.bio || '',
      location: user.location,
      store_name: user.store_name || '',
      store_description: user.store_description || ''
    });
    setShowEditModal(true);
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    const updates = { name: editForm.name, bio: editForm.bio, location: editForm.location };
    if (user.is_store) {
      updates.store_name = editForm.store_name;
      updates.store_description = editForm.store_description;
    }
    try {
      const res = await fetch(`${API_URL}/api/auth/user/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (!res.ok) throw new Error();
      updateProfile(updates);
      toast.success('Profile updated!');
      setShowEditModal(false);
    } catch {
      toast.error('Could not save your profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const base64 = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const size = 300;
          const min = Math.min(img.width, img.height);
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, (img.width - min) / 2, (img.height - min) / 2, min, min, 0, 0, size, size);
          resolve(canvas.toDataURL('image/jpeg', 0.82));
        };
        img.onerror = () => resolve(ev.target.result);
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });
    try {
      const res = await fetch(`${API_URL}/api/auth/user/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ avatar: base64 })
      });
      if (!res.ok) throw new Error();
      updateProfile({ avatar: base64 });
      toast.success('Profile photo updated!');
    } catch {
      toast.error('Could not update your photo. Please try again.');
    }
  };

  const handleConvertToStore = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/${user.id}/convert-to-store`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_name: storeForm.store_name || user.name,
          store_description: storeForm.store_description
        })
      });
      
      if (response.ok) {
        updateProfile({
          is_store: true,
          store_name: storeForm.store_name || user.name,
          store_description: storeForm.store_description
        });
        toast.success('Congratulations! Your store is now live! 🎉');
        setShowStoreModal(false);
        // Refresh user data
        window.location.reload();
      } else {
        toast.error('Failed to create store');
      }
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  const handleRoleSwitch = () => {
    switchRole();
    toast.success(`Switched to ${user.role === 'buyer' ? 'Seller' : 'Buyer'} mode`);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Signed out successfully');
  };

  const menuItems = [
    { icon: Wallet, label: 'Wallet', path: '/wallet', badge: `$${walletBalance.toFixed(0)}` },
    { icon: Package, label: 'My Orders', path: '/orders', badge: null },
    { icon: Heart, label: 'Wishlist', path: '/wishlist', badge: null },
    { icon: ShoppingBag, label: 'My Listings', path: '/my-listings', badge: myListings.length },
    { icon: TrendingUp, label: 'Seller Analytics', path: '/seller/analytics', badge: null },
    { icon: Users, label: 'Invite Friends', path: '/invite', badge: '$5 reward' },
    { icon: Star, label: 'Reviews', path: null, action: 'reviews', badge: user.reviewCount },
    { icon: Bell, label: 'Notification Settings', path: null, action: 'notifications' },
    { icon: Shield, label: 'Privacy Policy', path: '/privacy', badge: null },
    { icon: HelpCircle, label: 'Terms of Service', path: '/terms', badge: null },
  ];

  return (
    <Layout title="Profile" showSearch={false}>
      {/* Founding Ten challenge tracker */}
      <FoundingTenBanner userId={user?.id} />

      {/* Profile Header */}
      <div className="bg-minted-900 px-4 py-6 text-white">
        <div className="flex items-start gap-4">
          <div className="relative">
            <img
              src={user.avatar}
              alt={user.name}
              className="w-20 h-20 rounded-sm object-cover border-2 border-minted-600"
            />
            <button
              onClick={() => avatarInputRef.current?.click()}
              className="absolute bottom-0 right-0 p-1.5 bg-minted-600 text-white rounded-sm shadow-lg"
              data-testid="change-avatar-btn"
              aria-label="Change profile photo"
            >
              <Camera className="w-4 h-4" />
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
              data-testid="avatar-file-input"
            />
            {user.verified && (
              <div className="absolute -top-1 -right-1 bg-white text-minted-900 p-1 rounded-sm">
                <Shield className="w-3 h-3" />
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-xl text-white">{user.name}</h1>
              {user.verified && (
                <Badge className="bg-minted-600/20 text-minted-200 text-[10px] rounded-sm border-0">Verified</Badge>
              )}
            </div>
            <p className="text-minted-300 text-sm mt-1">{user.bio || 'No bio yet'}</p>
            {user.is_store && (
              <button
                onClick={() => navigate(`/store/${user.id}`)}
                className="flex items-center gap-1.5 mt-2 text-xs text-minted-200 bg-white/10 px-2 py-1 rounded-sm"
                data-testid="profile-shop-name"
              >
                <Store className="w-3.5 h-3.5" />
                <span className="font-medium">{user.store_name || user.name}</span>
                <ChevronRight className="w-3 h-3" />
              </button>
            )}
            <div className="flex items-center gap-4 mt-2 text-xs text-minted-400">
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {user.location}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                Joined {new Date(user.joinedDate).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}
              </span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-3 mt-6">
          <div className="flex-1 bg-white/10 rounded-sm p-3 text-center">
            <p className="text-xl font-display text-white">{user.salesCount}</p>
            <p className="text-[10px] text-minted-400 tracking-wider uppercase">Sales</p>
          </div>
          <div className="flex-1 bg-white/10 rounded-sm p-3 text-center">
            <p className="text-xl font-display text-white flex items-center justify-center gap-1">
              {user.rating}
              <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
            </p>
            <p className="text-[10px] text-minted-400 tracking-wider uppercase">Rating</p>
          </div>
          <div className="flex-1 bg-white/10 rounded-sm p-3 text-center">
            <p className="text-xl font-display text-white">{user.reviewCount}</p>
            <p className="text-[10px] text-minted-400 tracking-wider uppercase">Reviews</p>
          </div>
        </div>

        {/* Edit Button */}
        <Button
          variant="outline"
          className="w-full mt-4"
          onClick={handleEditOpen}
        >
          <Edit className="w-4 h-4 mr-2" />
          Edit Profile
        </Button>
      </div>

      {/* Role Switcher */}
      <div className="bg-white mt-2 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-minted-100 rounded-lg">
              <RefreshCw className="w-5 h-5 text-minted-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Switch Role</p>
              <p className="text-sm text-gray-500">Currently: <span className="capitalize font-medium text-minted-600">{user.role}</span></p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRoleSwitch}
          >
            Switch to {user.role === 'buyer' ? 'Seller' : 'Buyer'}
          </Button>
        </div>
      </div>

      {/* Menu Items */}
      <div className="bg-white mt-2">
        {menuItems.map((item, index) => (
          <button
            key={index}
            onClick={() => {
              if (item.path) {
                navigate(item.path);
              } else if (item.action === 'notifications') {
                document.getElementById('notification-prefs')?.scrollIntoView({ behavior: 'smooth' });
              } else if (item.action === 'reviews') {
                document.getElementById('reviews-section')?.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="w-full flex items-center justify-between px-4 py-4 hover:bg-gray-50 transition-colors border-b last:border-b-0"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <item.icon className="w-5 h-5 text-gray-600" />
              </div>
              <span className="font-medium text-gray-900">{item.label}</span>
            </div>
            <div className="flex items-center gap-2">
              {item.badge !== null && item.badge > 0 && (
                <Badge variant="secondary" className="text-xs">{item.badge}</Badge>
              )}
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </button>
        ))}
      </div>

      {/* Become a Store Section - Only show if not already a store */}
      {!user.is_store && (
        <div className="bg-white mt-2 px-4 py-4">
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-600 rounded-lg">
                <Store className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-purple-900">Become a Store</h3>
                <p className="text-sm text-purple-700 mt-1">
                  Got a business or lots of stock to sell? Create a store profile to stand out!
                </p>
                <div className="mt-3 p-3 bg-white/60 rounded-lg">
                  <p className="text-xs text-purple-800 font-medium mb-2">🏪 What&apos;s the difference?</p>
                  <ul className="text-xs text-purple-700 space-y-1">
                    <li>• <strong>Sellers</strong> = Individuals selling a few items</li>
                    <li>• <strong>Stores</strong> = Businesses with regular stock/inventory</li>
                  </ul>
                </div>
                <Button
                  size="sm"
                  className="mt-3 bg-purple-600 hover:bg-purple-700"
                  onClick={() => setShowStoreModal(true)}
                >
                  Set Up My Store
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Store Badge - Show if already a store */}
      {user.is_store && (
        <div className="bg-white mt-2 px-4 py-4">
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl p-4 text-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Store className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Store Account</h3>
                <p className="text-sm text-purple-200">{user.store_name || user.name}</p>
              </div>
              <Badge className="bg-white/20 text-white">Verified Store</Badge>
            </div>
          </div>
        </div>
      )}

      {/* Reviews Section */}
      <div className="bg-white mt-2 px-4 py-4" id="reviews-section">
        <h3 className="font-semibold text-gray-900 mb-3">Reviews</h3>
        {user.reviewCount > 0 ? (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className={`w-4 h-4 ${i <= Math.round(user.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />
              ))}
            </div>
            <span className="text-sm text-gray-600">{user.rating?.toFixed(1) || '0.0'} ({user.reviewCount} reviews)</span>
          </div>
        ) : (
          <div className="text-center py-6">
            <Star className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No reviews yet</p>
            <p className="text-xs text-gray-400 mt-1">Reviews appear here when buyers rate your sales</p>
          </div>
        )}
      </div>

      {/* Notification Preferences */}
      <div className="bg-white mt-2 px-4 py-4" id="notification-prefs">
        <h3 className="font-semibold text-gray-900 mb-4">Notification Preferences</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Email Notifications</p>
              <p className="text-sm text-gray-500">Receive updates via email</p>
            </div>
            <Switch
              checked={user.notificationPreferences?.email}
              onCheckedChange={(checked) => updateNotificationPreferences({ email: checked })}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Push Notifications</p>
              <p className="text-sm text-gray-500">
                {isNotificationEnabled() ? 'Receiving push alerts' : 'Tap to enable browser push alerts'}
              </p>
            </div>
            <Switch
              checked={isNotificationEnabled() && user.notificationPreferences?.push}
              onCheckedChange={async (checked) => {
                if (checked) {
                  const granted = await requestNotificationPermission();
                  if (granted) {
                    toast.success('Push notifications enabled!');
                    updateNotificationPreferences({ push: true });
                  } else if (typeof window !== 'undefined' && !('Notification' in window)) {
                    toast.error('Push isn\'t supported in this app — open mintedbuysell.com.au in Chrome to enable alerts.');
                  } else if (!isPushServiceAvailable()) {
                    toast.error('Notification service is temporarily unavailable — you will still receive email alerts.');
                  } else {
                    toast.error('Please allow notifications in your browser settings');
                  }
                } else {
                  updateNotificationPreferences({ push: false });
                }
              }}
              data-testid="push-notification-toggle"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">In-App Notifications</p>
              <p className="text-sm text-gray-500">Show notifications in app</p>
            </div>
            <Switch
              checked={user.notificationPreferences?.inApp}
              onCheckedChange={(checked) => updateNotificationPreferences({ inApp: checked })}
            />
          </div>
        </div>
      </div>

      {/* Sign Out */}
      <div className="px-4 py-6">
        <Button
          variant="outline"
          className="w-full text-red-600 border-red-200 hover:bg-red-50"
          onClick={handleLogout}
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </div>

      {/* Edit Profile Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Bio</Label>
              <Textarea
                value={editForm.bio}
                onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                placeholder="Tell buyers about yourself..."
                className="mt-1"
                rows={3}
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={editForm.location}
                onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                className="mt-1"
              />
            </div>
            {user.is_store && (
              <>
                <div>
                  <Label>Shop Name</Label>
                  <Input
                    value={editForm.store_name}
                    onChange={(e) => setEditForm({ ...editForm, store_name: e.target.value })}
                    placeholder="Your shop name"
                    className="mt-1"
                    data-testid="edit-shop-name-input"
                  />
                </div>
                <div>
                  <Label>Shop Description</Label>
                  <Textarea
                    value={editForm.store_description}
                    onChange={(e) => setEditForm({ ...editForm, store_description: e.target.value })}
                    placeholder="What does your shop sell?"
                    className="mt-1"
                    rows={2}
                    data-testid="edit-shop-description-input"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button className="bg-minted-600 hover:bg-minted-700" onClick={handleSaveProfile} disabled={savingProfile} data-testid="save-profile-btn">
              {savingProfile ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Become a Store Modal */}
      <Dialog open={showStoreModal} onOpenChange={setShowStoreModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="w-5 h-5 text-purple-600" />
              Set Up Your Store
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-purple-50 p-3 rounded-lg text-sm text-purple-800">
              <p className="font-medium mb-1">🏪 Store accounts are for:</p>
              <ul className="text-xs space-y-1 ml-2">
                <li>• Small businesses with inventory</li>
                <li>• Resellers with regular stock</li>
                <li>• Suppliers or wholesalers</li>
              </ul>
              <p className="mt-2 text-xs text-purple-600">
                You can still sell individual items as a regular seller without a store.
              </p>
            </div>
            <div>
              <Label>Store Name</Label>
              <Input
                value={storeForm.store_name}
                onChange={(e) => setStoreForm({ ...storeForm, store_name: e.target.value })}
                placeholder={user?.name || "Your Store Name"}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Store Description</Label>
              <Textarea
                value={storeForm.store_description}
                onChange={(e) => setStoreForm({ ...storeForm, store_description: e.target.value })}
                placeholder="What does your store sell? Tell buyers about your business..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStoreModal(false)}>
              Cancel
            </Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleConvertToStore}>
              Create My Store
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ProfilePage;